import { useEffect, useMemo } from 'react';

import {
  useGetTeamsQuery,
  useProjectsQuery,
} from '@app-velocity/libs/teams-data-access';

import { useCurrentTeamStore } from '../../store/useCurrentTeamStore';

export const useCurrentTeam = (
  userId: string | undefined,
  projectId: string | undefined,
  teamId: string | undefined,
) => {
  const { data: projects, isLoading: areProjectsLoading } =
    useProjectsQuery(userId);
  const {
    data: teams,
    isLoading: areTeamsLoading,
    refetch: refetchUserTeams,
  } = useGetTeamsQuery();
  const { setCurrentWorkspaceId, setCurrentTeamId } = useCurrentTeamStore();

  const saveWorkspaceId = (workspaceId: string) => {
    setCurrentWorkspaceId(workspaceId);
  };

  const saveTeamId = (teamId: string) => {
    setCurrentTeamId(teamId);
  };

  useEffect(() => {
    if (!userId) return;
    refetchUserTeams();
  }, [userId]);

  const activeTeam = useMemo(() => {
    if (!teams) return null;

    const activeTeam =
      teams && teamId ? teams?.find((team) => team.teamId === teamId) : null;

    return activeTeam || teams[0];
  }, [teamId, teams]);

  const activeWorkspace = useMemo(() => {
    if (!projects || !activeTeam) return null;

    if (!projectId) {
      return projects.find((project) => project.teamId === activeTeam.teamId);
    }

    const activeProjectFromTeam = projects.find(
      (project) =>
        project.teamId === activeTeam.teamId && project.id === projectId
    );

    if (activeProjectFromTeam) return activeProjectFromTeam;

    return projects.find((project) => project.teamId === activeTeam.teamId);
  }, [projectId, projects, activeTeam]);

  return {
    saveWorkspaceId,
    saveTeamId,
    isLoading: areTeamsLoading || areProjectsLoading,
    activeWorkspace,
    activeTeam,
  };
};
